import React from 'react';
import PropTypes from 'prop-types';
import { Avatar, Box, Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AccessTime as ClockIcon,
  FitnessCenter as CategoryIcon,
  Place as PlaceIcon,
  Videocam as OnlineIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { differenceInMinutes, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { timeFormat } from 'common/constants/date';
import { convertUtcToLocalTime } from 'common/utils/date';
import { getAbsenteeLabel } from 'common/utils/sessionColor';
import { getCategoryDescription } from 'common/constants/sessionCategoryDescriptions';
import SessionStatusChip from 'components/session/SessionStatusChip';
import SessionProgressDonut from 'components/session/SessionProgressDonut';

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function DetailRow({ icon, label = null, value, sub = null }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ color: 'secondary.main', mt: 0.25, display: 'flex' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        {label && <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{label}</Typography>}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
        {sub && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>}
      </Box>
    </Stack>
  );
}
DetailRow.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string,
  value: PropTypes.node.isRequired,
  sub: PropTypes.string
};

/**
 * Panel de detalle de UNA sesión (vista cliente). Datos + ubicación derivada (Salón/Online)
 * + "sobre esta clase" + donut de progreso 30 días. Mode-aware, sin hex suelto.
 */
function SessionDetailPanel({
  session,
  displayStatus = null,
  dateLocale,
  progress = null,
  person = null,
  personLabel = null,
  actionNode = null,
  onViewMore = null,
  onClose = null,
  onBack = null,
  onGoToAgenda = null
}) {
  const { t, i18n } = useTranslation(['sessions', 'labels']);

  const status = displayStatus ?? session.status;
  const fullDate = capitalize(convertUtcToLocalTime(session.start_date, "EEEE d 'de' MMMM, yyyy", dateLocale));
  const startTime = convertUtcToLocalTime(session.start_date, timeFormat);
  const endTime = convertUtcToLocalTime(session.end_date, timeFormat);
  const minutes = session.end_date && session.start_date
    ? differenceInMinutes(parseISO(session.end_date), parseISO(session.start_date))
    : null;
  // `person` permite mostrar al CLIENTE (vista entrenador) en lugar del entrenador (vista cliente).
  const who = person || session.trainer || session.trainer_id;
  const whoLabel = personLabel || t('trainer', { count: 1, ns: 'labels', defaultValue: 'Entrenador' });
  const category = session.session_type_id?.name;
  // Quién faltó (Cliente/Entrenador) cuando la sesión es una ausencia atribuida.
  const absentee = getAbsenteeLabel(t, status);

  // La ubicación (Online/Salón) depende SIEMPRE del entrenador de la sesión, no de `who`.
  const sessionTrainer = session.trainer || session.trainer_id;
  const isOnline = Boolean(sessionTrainer?.online_meeting_url);
  const locationValue = isOnline
    ? t('online', { ns: 'sessions', defaultValue: 'Online' })
    : t('mainHall', { ns: 'sessions', defaultValue: 'Salón Principal' });
  const locationSub = isOnline
    ? t('onlineSubtitle', { ns: 'sessions', defaultValue: 'Sesión virtual' })
    : 'OTRIX Wellness Club';

  const about = session.observations
    || getCategoryDescription(category, i18n.language)
    || t('aboutClassFallback', { ns: 'sessions', defaultValue: 'Entrenamiento funcional para mejorar fuerza, resistencia y condición física.' });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {onBack && (
            <IconButton size="small" onClick={onBack} aria-label={t('back', { ns: 'sessions', defaultValue: 'Volver' })} sx={{ color: 'text.secondary' }}>
              <BackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t('sessionDetail', { ns: 'sessions', defaultValue: 'Detalle de la sesión' })}
          </Typography>
        </Stack>
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label={t('close', { ns: 'sessions', defaultValue: 'Cerrar' })} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <SessionStatusChip status={status} startDate={session.start_date} />
          {absentee && (
            <Typography variant="caption" sx={{ color: (th) => th.palette.sessionStatus.absent.fg, fontWeight: 700 }}>
              {absentee}
            </Typography>
          )}
        </Stack>
        {onGoToAgenda && (
          <Button size="small" variant="text" color="inherit" startIcon={<CalendarIcon fontSize="small" />} onClick={onGoToAgenda} sx={{ color: 'secondary.main', textTransform: 'none', fontWeight: 700 }}>
            {t('goToAgenda', { ns: 'sessions', defaultValue: 'Ir a agenda' })}
          </Button>
        )}
      </Stack>

      {actionNode && <Box sx={{ mb: 2 }}>{actionNode}</Box>}

      <Stack spacing={2}>
        <DetailRow icon={<CalendarIcon fontSize="small" />} value={fullDate} />
        <DetailRow
          icon={<ClockIcon fontSize="small" />}
          value={`${startTime} – ${endTime}`}
          sub={minutes ? `${minutes} ${t('minutes', { ns: 'sessions', defaultValue: 'minutos' })}` : null}
        />
        {who && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar src={who.photo} alt={who.full_name} sx={{ width: 34, height: 34, fontSize: '0.85rem' }}>
              {who.full_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{who.full_name}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{whoLabel}</Typography>
            </Box>
          </Stack>
        )}
        {category && <DetailRow icon={<CategoryIcon fontSize="small" />} label={t('category', { ns: 'labels', defaultValue: 'Categoría' })} value={category} />}
        <DetailRow
          icon={isOnline ? <OnlineIcon fontSize="small" /> : <PlaceIcon fontSize="small" />}
          label={t('location', { ns: 'sessions', defaultValue: 'Ubicación' })}
          value={locationValue}
          sub={locationSub}
        />
      </Stack>

      <Divider sx={{ my: 2.5 }} />

      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1 }}>
        <Box sx={{ color: 'secondary.main', mt: 0.25, display: 'flex' }}><InfoIcon fontSize="small" /></Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
            {t('aboutClass', { ns: 'sessions', defaultValue: 'Sobre esta clase' })}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{about}</Typography>
        </Box>
      </Stack>

      {progress && (
        <>
          <Divider sx={{ my: 2.5 }} />
          <SessionProgressDonut
            completed={progress.completed}
            scheduled={progress.scheduled}
            absent={progress.absent}
            onViewMore={onViewMore}
          />
        </>
      )}
    </Box>
  );
}

SessionDetailPanel.propTypes = {
  session: PropTypes.object.isRequired,
  displayStatus: PropTypes.string,
  dateLocale: PropTypes.object.isRequired,
  progress: PropTypes.shape({
    completed: PropTypes.number,
    scheduled: PropTypes.number,
    absent: PropTypes.number
  }),
  person: PropTypes.object,
  personLabel: PropTypes.string,
  actionNode: PropTypes.node,
  onViewMore: PropTypes.func,
  onClose: PropTypes.func,
  onBack: PropTypes.func,
  onGoToAgenda: PropTypes.func
};

export default SessionDetailPanel;
